import { Base, createApp, importWhenVisible } from '@studiometa/js-toolkit';
import { FigureTwicpics } from '@studiometa/ui';
import Header from './organisms/Header.js';
import Typeform from './molecules/Typeform.js';
import LazyHydrate from './atoms/LazyHydrate.js';
import { isDev } from './config.js';

/**
 * @typedef {{
 *   $children: {
 *     Header: Header[];
 *     Figure: FigureTwicpics[];
 *     Typeform: Typeform[];
 *     LazyHydrate: LazyHydrate[];
 *     Search: Array<Promise<import('./organisms/Search/index.js').default>>;
 *     SearchPrice: Array<Promise<import('./organisms/Search/SearchPrice.js').default>>;
 *     GalleryGrid: Array<Promise<import('./molecules/GalleryGrid.js').default>>;
 *     GallerySlider: Array<Promise<import('./molecules/GallerySlider.js').default>>;
 *     GalleryBtn: Array<Promise<import('./atoms/GalleryBtn.js').default>>;
 *     Slider: Array<Promise<import('./molecules/Slider.js').default>>;
 *     Loadmore: Array<Promise<import('./molecules/Loadmore.js').default>>;
 *     CardSlider: Array<Promise<import('./molecules/CardSlider.js').default>>;
 *     TinyHouseList: Array<Promise<import('./molecules/TinyHouseList.js').default>>;
 *     ObfuscedLink: Array<Promise<import('./atoms/ObfuscedLink.js').default>>;
 *     Readmore: Array<Promise<import('./atoms/Readmore.js').default>>;
 *     Video: Array<Promise<import('./molecules/Video.js').default>>;
 *     Accordion: Array<Promise<import('./molecules/Accordion.js').default>>;
 *     Map: Array<Promise<import('./organisms/Map/index.js').default>>;
 *   }
 * }} AppProps
 */

/**
 * Main App class.
 * @augments {Base<AppProps>}
 */
class App extends Base {
  /**
   * App config.
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    log: isDev(),
    name: 'App',
    components: {
      Figure: FigureTwicpics,
      Search: () => import('./organisms/Search/index.js'), // Critical, must be loaded as fast as possible
      SearchPrice: () => import('./organisms/Search/SearchPrice.js'), // Critical, must be loaded as fast as possible
      Header,
      GalleryGrid: () => import('./molecules/GalleryGrid.js'),
      GallerySlider: () => import('./molecules/GallerySlider.js'),
      GalleryBtn: () => import('./atoms/GalleryBtn.js'),
      Slider: (app) => importWhenVisible(() => import('./molecules/Slider.js'), 'Slider', app),
      Loadmore: () => import('./molecules/Loadmore.js'),
      CardSlider: () => import('./molecules/CardSlider.js'),
      TinyHouseList: () => import('./molecules/TinyHouseList.js'),
      ObfuscedLink: (app) =>
        importWhenVisible(() => import('./atoms/ObfuscedLink.js'), 'ObfuscedLink', app),
      Readmore: (app) => importWhenVisible(() => import('./atoms/Readmore.js'), 'Readmore', app),
      Video: (app) => importWhenVisible(() => import('./molecules/Video.js'), 'Video', app),
      Accordion: (app) =>
        importWhenVisible(() => import('./molecules/Accordion.js'), 'Accordion', app),
      Map: (app) => importWhenVisible(() => import('./organisms/Map/index.js'), 'Map', app),
      Typeform,
      LazyHydrate,
    },
  };

  /**
   * Get the gallery lazy hydrator.
   * @returns {LazyHydrate|null}
   */
  get galleryLazyHydrate() {
    for (const instance of this.$children.LazyHydrate) {
      if (instance.$el.id === 'gallery-grid-lazy-hydrate') {
        return instance;
      }
    }

    return null;
  }

  /**
   * When click on gallery btn.
   */
  async onGalleryBtnGalleryOpen() {
    const { galleryLazyHydrate } = this;

    if (galleryLazyHydrate) {
      await galleryLazyHydrate.hydrate();
    }

    const galleryGrid = await this.$children.GalleryGrid[0];
    galleryGrid.open();
  }

  /**
   * When click on gallery slider btn.
   * @param {number} index
   */
  async onGalleryGridOpenGallerySlider(index) {
    const gallerySlider = await this.$children.GallerySlider[0];
    gallerySlider.open();
    gallerySlider.goTo(index);
  }

  /**
   * Update component.
   */
  onLoadmorePostsLoaded() {
    this.$update();
  }
}

export default createApp(App, {
  features: {
    asyncChildren: true,
  },
});
